import Icon from 'gu-ui-kit/Icon'
import React, { lazy } from 'react'
import { ROUTES } from 'constants/routes'
import { routes as commonRoutes, withAccess, ROUTE_ACCESS } from '../common/router'
import mergeArraysByKey from 'utils/mergeArraysByKey'

const Viewer = lazy(() => import(/* webpackChunkName: "page-viewer" */ 'modules/Viewer'))
const Pulse = lazy(() => import(/* webpackChunkName: "page-statistics" */ 'modules/Statistics/Pulse'))
const AdvertisersList = lazy(() => import(/* webpackChunkName: "page-advertisers" */ 'modules/Advertisers/List'))
const AdvertiserCreate = lazy(() => import(/* webpackChunkName: "page-advertisers" */ 'modules/Advertisers/Create'))
const PartnersProgram = lazy(() => import(/* webpackChunkName: "page-partners" */ 'modules/PartnersProgram'))
const RecommendationsWrapper = lazy(() =>
  import(/* webpackChunkName: "page-recommendations" */ 'modules/Recommendations/RecommendationsWrapper'),
)
const RecommendationsAudits = lazy(() => import(/* webpackChunkName: "page-recommendations" */ 'modules/Recommendations/Audits'))
const RecommendationsBenchmarks = lazy(() => import(/* webpackChunkName: "page-recommendations" */ 'modules/Recommendations/Benchmarks'))
const RecommendationsTypesList = lazy(
  () => import(/* webpackChunkName: "page-recommendations" */ 'modules/Recommendations/CampaignsRecommendations/TypesList'),
)
const RecommendationsType = lazy(
  () => import(/* webpackChunkName: "page-recommendations" */ 'modules/Recommendations/CampaignsRecommendations/Type'),
)
const TransferAccount = lazy(() =>
  import(/* webpackChunkName: "page-account-transfer" */ 'modules/Services/TransferAccount'),
)
const TransferAccountPayment = lazy(() =>
  import(
    /* webpackChunkName: "page-transfer-account-payment" */ 'modules/Billing/TransferAccountPayment'
  ),
)
const ServicesNetworkLeadManager = lazy(() =>
  import(/* webpackChunkName: "page-services-lead-manager" */ 'modules/Services/LeadManager'),
)
const ServicesNetworkLeadManagerWizard = lazy(() =>
  import(/* webpackChunkName: "page-services-lead-manager" */ 'modules/Services/LeadManager/Wizard'),
)
const ServicesNetworkLeadManagerCreateWebhook = lazy(() =>
  import(
    /* webpackChunkName: "page-services-lead-manager" */ 'modules/Services/LeadManager/CreateWebhook'
  ),
)
const AmoCrmCode = lazy(() =>
  import(/* webpackChunkName: "page-return-url-amocrm" */ 'modules/ReturnUrl/AmoCrmCode'),
)

const productRoutes = withAccess(
  [
  {
    path: ROUTES.AMO_CRM_CODE,
    component: () => <AmoCrmCode />,
    key: 'amo-crm-code',
  },
  {
    restyled: true,
    path: [ROUTES.SERVICES_NETWORK_LEAD_MANAGER, ROUTES.SERVICES_NETWORK_LEAD_MANAGER_JOURNAL],
    exact: true,
    key: 'service_lead_manager_page',
    label_key: 'lead_manager::link',
    component: ServicesNetworkLeadManager,
    domain: 'services',
  },
  {
    restyled: true,
    path: [
      ROUTES.SERVICES_NETWORK_LEAD_MANAGER_CREATE,
      ROUTES.SERVICES_NETWORK_LEAD_MANAGER_DUPLICATE,
    ],
    exact: true,
    key: 'service_lead_manager_wizard',
    label_key: 'lead_manager::link',
    component: ServicesNetworkLeadManagerWizard,
    layout: 'wide',
    domain: 'services',
  },
  {
    restyled: true,
    path: ROUTES.SERVICES_NETWORK_LEAD_MANAGER_CREATE_WEBHOOK,
    exact: true,
    key: 'service_lead_manager_create_webhook',
    label_key: 'lead_manager::link',
    component: ServicesNetworkLeadManagerCreateWebhook,
    layout: 'wide',
    domain: 'services',
  },
  {
    path: ROUTES.SERVICES_NETWORK_ACCOUNT_TRANSFER,
    exact: true,
    key: 'service_account_transfer',
    label_key: 'account_transfer::title',
    component: TransferAccount,
    layout: 'custom',
    restyled: true,
    domain: 'services',
  },
  {
    layout: 'wide',
    exact: true,
    path: ROUTES.BILLING_ACCOUNT_TRANSFER_PAYMENT,
    key: 'billing/account-transfer-payment',
    label_key: 'account_transfer::payment.title',
    component: TransferAccountPayment,
    restyled: true,
    domain: 'finance',
  },
  {
    restyled: true,
    path: [
      ROUTES.SERVICES_NETWORK,
      ROUTES.SERVICES_NETWORK_TRANSFER_ACCOUNTS,
      ROUTES.SERVICES_NETWORK_META_PIXELS,
    ],
    exact: true,
    key: 'service_page',
    domain: 'services',
  },
  {
    hidden: true,
    exact: true,
    path: ROUTES.RECOMMENDATIONS_AUDITS_VIEW,
    key: 'recommendations/audits/view',
    page_title_keys: ['recommendations::navigation.main', 'recommendations::navigation.audits'],
    component: Viewer,
    layout: 'custom',
    domain: 'recommendations',
  },
  {
    hidden: true,
    exact: true,
    path: ROUTES.RECOMMENDATIONS_BENCHMARKS_VIEW,
    key: 'recommendations/benchmarks/view',
    page_title_keys: ['recommendations::navigation.main', 'recommendations::navigation.benchmarks'],
    component: Viewer,
    layout: 'custom',
    domain: 'recommendations',
  },
  {
    path: ROUTES.ADVERTISER_NEW,
    exact: true,
    restyled: true,
    key: 'advertisers/create',
    label_key: 'advertisers::form.header_new',
    page_title_keys: ['advertisers::heading', 'advertisers::form.header_new'],
    component: AdvertiserCreate,
    domain: 'advertisers',
    icon: (
      <Icon
        name={'my-agency'}
        size={'m'}
      />
    ),
    layout: 'wide',
  },
  {
    path: ROUTES.ADVERTISER_EDIT,
    exact: true,
    restyled: true,
    key: 'advertisers/edit',
    label_key: 'advertisers::form.header_edit',
    page_title_keys: ['advertisers::heading', 'advertisers::form.header_edit'],
    component: AdvertiserCreate,
    domain: 'advertisers',
    icon: (
      <Icon
        name={'my-agency'}
        size={'m'}
      />
    ),
    layout: 'wide',
  },
  {
    restyled: true,
    exact: true,
    path: ROUTES.ADVERTISERS,
    key: 'advertisers_list',
    label_key: 'advertisers::heading',
    component: AdvertisersList,
    domain: 'advertisers',
    icon: (
      <Icon
        name={'user'}
        size={'m'}
      />
    ),
  },

  {
    path: ROUTES.PULSE,
    exact: true,
    label_key: 'common::nav_menu.pulse',
    key: 'statistics_pulse',
    component: Pulse,
    domain: 'statistics',
    icon: (
      <Icon
        name={'pulse'}
        size={'m'}
      />
    ),
    restyled: true,
  },
  {
    path: ROUTES.PARTNERS,
    label_key: 'common::nav_menu.partners',
    key: 'partners',
    component: PartnersProgram,
    domain: 'partners',
    restyled: true,
    icon: (
      <Icon
        name={'money'}
        size={'m'}
      />
    ),
  },
  {
    restyled: true,
    hidden: true,
    exact: true,
    path: ROUTES.RECOMMENDATIONS,
    key: 'recommendations/redirect',
    component: RecommendationsWrapper,
    domain: 'recommendations',
    icon: (
      <Icon
        name={'thumb-up'}
        size={'m'}
      />
    ),
  },
  {
    restyled: true,
    exact: true,
    path: ROUTES.RECOMMENDATIONS_AUDITS,
    key: 'recommendations/audits',
    label_key: 'recommendations::navigation.audits',
    page_title_keys: ['recommendations::navigation.main', 'recommendations::navigation.audits'],
    component: RecommendationsAudits,
    domain: 'recommendations',
    icon: (
      <Icon
        name={'thumb-up'}
        size={'m'}
      />
    ),
  },
  {
    restyled: true,
    exact: true,
    path: ROUTES.RECOMMENDATIONS_BENCHMARKS,
    key: 'recommendations/benchmarks',
    label_key: 'recommendations::navigation.benchmarks',
    page_title_keys: ['recommendations::navigation.main', 'recommendations::navigation.benchmarks'],
    component: RecommendationsBenchmarks,
    domain: 'recommendations',
    icon: (
      <Icon
        name={'bar-chart'}
        size={'m'}
      />
    ),
  },
  {
    restyled: true,
    exact: true,
    path: ROUTES.RECOMMENDATIONS_CAMPAIGNS,
    key: 'recommendations/campaigns',
    label_key: 'recommendations::navigation.campaigns',
    page_title_keys: ['recommendations::navigation.main', 'recommendations::navigation.campaigns'],
    component: RecommendationsTypesList,
    domain: 'recommendations',
    icon: (
      <Icon
        name={'thumb-up'}
        size={'m'}
      />
    ),
  },
  {
    restyled: true,
    exact: true,
    path: ROUTES.RECOMMENDATIONS_TYPE,
    key: 'recommendations/type',
    component: RecommendationsType,
    domain: 'recommendations',
  },
  ],
  ROUTE_ACCESS.PRIVATE,
)

const routes = mergeArraysByKey(commonRoutes, productRoutes, 'key')

export default routes
export { routes }
